<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/onenetwork/stores/onenetwork.store.auth.ts'
import { useEstateProjectListStore } from '@/modules/estate-project/stores/estate-project-list-store'
import { useUpdateUrlParam } from '@/composables/useReactiveQueryParam'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import { Icon } from '@iconify/vue'
import { useConfirm } from 'primevue/useconfirm'
import {
  EstateProjectDetailActionType,
  useEstateProjectDetailStore,
} from '@/modules/estate-project/stores/estate-project-detail-store'
import { estateProjectSchema } from '@/modules/estate-project/helper/estate-project-schema'
import ProjectCoordinateMapPopup from '@/modules/estate-project/components/project-coordinate-map-popup.vue'
import ProjectInfoRowsEditor from '@/modules/estate-project/components/project-info-rows-editor.vue'
import ProjectDescriptionEditor from '@/modules/estate-project/components/project-description-editor.vue'
import ProjectGalleryEditor from '@/modules/estate-project/components/project-gallery-editor.vue'
import InvestorPickerWithCreatePanel from '@/modules/estate-project/components/investor-picker-with-create-panel.vue'
import CsButton from '@/components/ui/button/CsButton.vue'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import type {
  EstateProjectFormValues,
  EstateProjectGeoJsonPolygon,
  EstateProjectMedia,
} from '@/modules/estate-project/types/estate-project-types'

const props = defineProps<{ id?: number | string }>()

const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const store = useEstateProjectDetailStore()
const listStore = useEstateProjectListStore()
const authStore = useAuthStore()
const { updateUrlParam } = useUpdateUrlParam()

const projectId = computed(() => {
  const sourceId = props.id ?? route.params.id
  return Number(sourceId)
})
const resolver = yupResolver(estateProjectSchema)
const formRef = ref()
const editable = ref(false)

const detailState = computed(() => store.projects[projectId.value])
const loading = computed(() => detailState.value?.action_type === EstateProjectDetailActionType.UPDATING)
const deleting = computed(() => detailState.value?.action_type === EstateProjectDetailActionType.DELETING)
const userPerms = computed<string[]>(() => authStore.user?.permissions || [])
const canView = computed(() => {
  return userPerms.value.includes('real_estate.list') || userPerms.value.includes('real_estate.edit')
})
const canEdit = computed(() => {
  return userPerms.value.includes('real_estate.edit')
})

const logoUuid = ref(uuid.v4().toString())

const markerPosition = computed(() => {
  const lat = Number(formRef.value?.fields?.lat?.states?.value)
  const lng = Number(formRef.value?.fields?.lng?.states?.value)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
})

const logoValue = computed(() => {
  return (formRef.value?.fields?.logo?.states?.value || null) as EstateProjectMedia | null
})

const polygonGeojsonValue = computed(() => {
  return (formRef.value?.fields?.polygon_geojson?.states?.value || null) as EstateProjectGeoJsonPolygon | null
})

const requestFetch = async () => {
  if (!projectId.value) return
  await store.fetchDetail(projectId.value)
}

onMounted(requestFetch)

watch(
  () => detailState.value?.formData,
  async (data) => {
    if (!data) return
    await nextTick()
    formRef.value?.reset()
  },
)

const handleSave = () => {
  if (!canEdit.value || loading.value) return
  formRef.value?.submit()
}

const handleReset = () => {
  editable.value = false
  formRef.value?.reset()
}

const onSubmit = async (e: any) => {
  const { valid } = e
  if (!valid || !canEdit.value) return

  const values: EstateProjectFormValues = e.values || Object.keys(e.states || {}).reduce((acc: any, key) => {
    acc[key] = e.states[key].value
    return acc
  }, {})

  confirm.require({
    message: 'Bạn có muốn lưu các chỉnh sửa?',
    header: 'Xác nhận',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: async () => {
      try {
        await store.updateProject(projectId.value, values)
        editable.value = false
      } catch {
        alert('Không thể cập nhật dự án')
      }
    },
  })
}

const handlePolygonConfirm = ({ polygon_geojson }: { polygon_geojson: EstateProjectGeoJsonPolygon }) => {
  if (!editable.value || !canEdit.value) return
  formRef.value?.setFieldValue('polygon_geojson', polygon_geojson)
}

const handlePolygonClear = () => {
  if (!editable.value || !canEdit.value) return
  formRef.value?.setFieldValue('polygon_geojson', null)
}

const handleProjectMarkerChange = ({ lat, lng }: { lat: number; lng: number }) => {
  if (!editable.value || !canEdit.value) return
  formRef.value?.setFieldValue('lat', lat)
  formRef.value?.setFieldValue('lng', lng)
}

const handleChooseLogo = (payload: any) => {
  if (!editable.value || !canEdit.value) return
  const media = payload?.response?.data || payload
  if (!media?.uuid) return
  formRef.value?.setFieldValue('logo', media)
  logoUuid.value = uuid.v4().toString()
}

const handleRemoveLogo = () => {
  if (!editable.value || !canEdit.value) return
  formRef.value?.setFieldValue('logo', null)
  logoUuid.value = uuid.v4().toString()
}

const handleDelete = async () => {
  if (!canEdit.value || deleting.value || loading.value) return
  const confirmed = window.confirm('Bạn có chắc muốn xoá dự án này?')
  if (!confirmed) return

  try {
    await store.deleteProject(projectId.value)
    await listStore.fetchList()
    listStore.closeTab(projectId.value)
    updateUrlParam('detailId', null)
    router.replace({ name: 'property-projects' })
  } catch {
    alert('Không thể xoá dự án')
  }
}
</script>

<template>
  <template v-if="detailState?.action_type === EstateProjectDetailActionType.FETCHING">
    <Card>
      <template #content>
        <div class="flex gap-3 items-center">
          Đang tải dữ liệu <Icon icon="eos-icons:three-dots-loading" width="24" height="24" />
        </div>
      </template>
    </Card>
  </template>

  <template v-else-if="detailState?.action_type === EstateProjectDetailActionType.FETCH_FAILED">
    <Card class="bg-red-300">
      <template #content>
        <div class="flex flex-col items-center gap-2">
          <Icon icon="fluent-color:error-circle-16" width="48" />
          {{ detailState?.action_message || 'Lỗi tải chi tiết dự án' }}
        </div>
      </template>
    </Card>
  </template>

  <template v-else-if="detailState?.formData && canView">
    <div class="estate-project-create-card">
      <div class="estate-project-action-bar">
        <div class="estate-project-action-group" v-if="canEdit">
          <CsButton
            type="button"
            text="Chỉnh sửa"
            icon="mynaui:edit-solid"
            @click="editable = true"
            :visible="!editable"
            :disabled="deleting"
          />
          <CsButton
            type="button"
            text="Lưu chỉnh sửa"
            icon="ri:save-3-fill"
            @click="handleSave"
            :visible="editable"
            :loading="loading"
          />
          <CsButton
            type="button"
            variant="danger"
            icon="ri:close-circle-fill"
            text="Hủy chỉnh sửa"
            @click="handleReset"
            :visible="editable"
            :disabled="loading"
          />
        </div>
      </div>

      <Form
        ref="formRef"
        v-slot="$form"
        :initialValues="detailState.formData"
        :resolver="resolver"
        @submit="onSubmit"
        class="estate-project-create-form"
      >
        <InputText name="lat" hidden />
        <InputText name="lng" hidden />
        <InputText name="polygon_geojson" hidden />
        <InputText name="logo" hidden />

        <div class="estate-project-create-left">
          <div class="estate-project-form-group">
            <label class="estate-project-label">Logo dự án <span>*</span></label>
            <div class="estate-project-logo-wrap">
              <ImagePicker
                :uuid="logoUuid"
                type="photo"
                folder="real_estate"
                width="120px"
                height="100px"
                :src="logoValue?.url?.medium || logoValue?.name"
                :previewSrc="logoValue?.url?.original"
                :readonly="!(editable && canEdit)"
                upload
                hideUploadPreview
                hideRemove
                previewIcon="sidekickicons:photo-plus-16-solid"
                @action:upload-success="handleChooseLogo"
                @action:choose-image="handleChooseLogo"
              />
              <button
                v-if="logoValue && editable && canEdit"
                type="button"
                class="estate-project-logo-remove"
                @click="handleRemoveLogo"
              >
                Gỡ logo
              </button>
            </div>
            <span class="estate-project-error">{{ $form?.logo?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group">
            <label class="estate-project-label">Tên dự án <span>*</span></label>
            <InputText name="project_name" class="form-control estate-project-input" :disabled="!editable" />
            <span class="estate-project-error">{{ $form?.project_name?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group">
            <label class="estate-project-label">Địa chỉ dự án <span>*</span></label>
            <InputText name="project_address" class="form-control estate-project-input" :disabled="!editable" />
            <span class="estate-project-error">{{ $form?.project_address?.error?.message }}</span>
          </div>

          <div class="estate-project-form-group estate-project-map-wrap">
            <ProjectCoordinateMapPopup
              mode="inline"
              :visible="true"
              :editable="editable && canEdit"
              :lat="Number($form.lat?.value || 10.7749)"
              :lng="Number($form.lng?.value || 106.7012)"
              :polygon-geojson="polygonGeojsonValue"
              :show-project-marker="true"
              :project-marker="markerPosition"
              map-class="estate-project-map"
              @confirm="handlePolygonConfirm"
              @clear="handlePolygonClear"
              @project-marker-change="handleProjectMarkerChange"
            />
            <span class="estate-project-error">{{ $form?.polygon_geojson?.error?.message }}</span>
          </div>
        </div>

        <div class="estate-project-create-right">
          <div class="estate-project-section">
            <InvestorPickerWithCreatePanel :form="formRef" :editable="editable && canEdit" />
          </div>

          <div class="estate-project-section">
            <ProjectInfoRowsEditor :form="formRef" :editable="editable && canEdit" />
          </div>

          <div class="estate-project-section">
            <ProjectDescriptionEditor :form="formRef" :editable="editable && canEdit" />
          </div>

          <div class="estate-project-section">
            <ProjectGalleryEditor :form="formRef" :editable="editable && canEdit" />
          </div>
        </div>

      </Form>
    </div>
  </template>

  <template v-else>
    <Card class="bg-red-300">
      <template #content>
        <div class="flex flex-col items-center gap-2">
          <Icon icon="fluent-color:error-circle-16" width="48" />
          Bạn không có quyền xem dự án này
        </div>
      </template>
    </Card>
  </template>
</template>

<style scoped>
.estate-project-create-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.14);
  padding: 32px;
}

.estate-project-create-form {
  display: grid;
  grid-template-columns: minmax(0, 585px) minmax(0, 1fr);
  gap: 32px;
}

.estate-project-create-left,
.estate-project-create-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.estate-project-form-group,
.estate-project-section {
  padding: 6px 0;
}

.estate-project-label {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-bottom: 10px;
}

.estate-project-label span {
  color: #ff0000;
}

.estate-project-error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}

:deep(.estate-project-input),
:deep(.estate-project-input input),
:deep(.estate-project-input .p-inputtext) {
  background: #f7f7f7 !important;
  border: 1px solid #f7f7f7 !important;
  border-radius: 4px;
  min-height: 44px;
}

.estate-project-map {
  width: 100%;
  height: 330px;
  border-radius: 8px;
  overflow: hidden;
}

.estate-project-logo-wrap {
  display: flex;
  gap: 12px;
  align-items: center;
}

.estate-project-logo-remove {
  border: 0;
  background: transparent;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}

:deep(.estate-project-map .gm-style-mtc),
:deep(.estate-project-map .gm-bundled-control),
:deep(.estate-project-map .gm-fullscreen-control),
:deep(.estate-project-map .gm-svpc),
:deep(.estate-project-map button[aria-label*='Map']),
:deep(.estate-project-map button[aria-label*='Satellite']),
:deep(.estate-project-map button[aria-label*='Bản đồ']),
:deep(.estate-project-map button[aria-label*='Vệ tinh']),
:deep(.estate-project-map [role='tablist']),
:deep(.estate-project-map [aria-label*='Zoom']),
:deep(.estate-project-map [aria-label*='Phóng to']),
:deep(.estate-project-map [aria-label*='Thu nhỏ']),
:deep(.estate-project-map [aria-label*='Street View']) {
  display: none !important;
}

.estate-project-action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 20px;
}

.estate-project-action-group {
  display: flex;
  gap: 8px;
}


@media (max-width: 1280px) {
  .estate-project-create-form {
    grid-template-columns: 1fr;
  }
}
</style>
